import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { Users, UserPlus, Dumbbell, Clock, ChevronRight, Award, CalendarDays, AlertCircle, Trophy, TrendingUp, FileBarChart } from "lucide-react";
import { statsApi } from "../services/api";
import { formatDate, getInitials } from "../lib/utils";
import TabelFotbalDialog from "../components/TabelFotbalDialog";
import RaportZileiDialog from "../components/RaportZileiDialog";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabelFotbalOpen, setTabelFotbalOpen] = useState(false);
  const [raportZileiOpen, setRaportZileiOpen] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await statsApi.get();
      setStats(data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="page-container">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  const sexData = stats?.sex_distribution || {};
  const sportData = stats?.sport_distribution || {};
  const totalPatients = stats?.total_patients || 0;
  const recentPatients = stats?.recent_patients || [];
  const totalVize = stats?.total_vize || 0;
  const vizeByDate = stats?.vize_by_date || [];
  const vizeThisMonth = stats?.vize_this_month || 0;
  const currentMonthLabel = stats?.current_month_label || "";
  const topClubs = stats?.top_clubs || [];
  const pendingVize = stats?.pending_vize || [];

  // Traducere RO pentru luna curentă (Mongo trimite "June 2026")
  const lunaRo = (() => {
    if (!currentMonthLabel) return "";
    const map = {
      January: "Ianuarie", February: "Februarie", March: "Martie", April: "Aprilie",
      May: "Mai", June: "Iunie", July: "Iulie", August: "August",
      September: "Septembrie", October: "Octombrie", November: "Noiembrie", December: "Decembrie",
    };
    const [m, y] = currentMonthLabel.split(" ");
    return `${map[m] || m} ${y}`;
  })();

  // Format a YYYY-MM-DD date string to Romanian: "19 mai 2027"
  const formatDateRo = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    const luni = [
      "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
      "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"
    ];
    const mi = parseInt(m, 10) - 1;
    return `${parseInt(d, 10)} ${luni[mi] || m} ${y}`;
  };

  // Get top sports
  const topSports = Object.entries(sportData)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <div data-testid="dashboard" className="page-container">
      {/* Page Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            Panou Principal
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestionarea bazei de date a sportivilor
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/admin/raport-antrenori">
            <Button variant="outline" className="gap-2 border-primary/40 hover:bg-primary/5" data-testid="btn-raport-antrenori">
              <Users className="w-4 h-4" />
              Raport Antrenori
            </Button>
          </Link>
          <Button
            variant="outline"
            className="gap-2 border-blue-500/60 text-blue-700 hover:bg-blue-50"
            onClick={() => setRaportZileiOpen(true)}
            data-testid="btn-raport-zilei"
          >
            <FileBarChart className="w-4 h-4" />
            Raportul zilei Înscrieri
          </Button>
          <Button
            variant="outline"
            className="gap-2 border-emerald-500/60 text-emerald-700 hover:bg-emerald-50"
            onClick={() => setTabelFotbalOpen(true)}
            data-testid="btn-tabel-fotbal"
          >
            <Trophy className="w-4 h-4" />
            Generează tabel fotbal
          </Button>
        </div>
      </div>

      <TabelFotbalDialog open={tabelFotbalOpen} onOpenChange={setTabelFotbalOpen} />
      <RaportZileiDialog open={raportZileiOpen} onOpenChange={setRaportZileiOpen} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Sportivi */}
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Total Sportivi
                </p>
                <p className="text-3xl font-bold mt-2" data-testid="total-patients">
                  {totalPatients}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Numar vize completate */}
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Numar Vize Completate
                </p>
                <p className="text-3xl font-bold mt-2 text-emerald-700" data-testid="total-vize">
                  {totalVize}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Award className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Distribuție Sex */}
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Distribuție Sex
                </p>
                <div className="flex gap-4 mt-2">
                  <div>
                    <span className="text-2xl font-bold">{sexData.Masculin || 0}</span>
                    <span className="text-sm text-muted-foreground ml-1">M</span>
                  </div>
                  <div>
                    <span className="text-2xl font-bold">{sexData.Feminin || 0}</span>
                    <span className="text-sm text-muted-foreground ml-1">F</span>
                  </div>
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Acțiuni Rapide */}
        <Card className="card-hover">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Acțiuni Rapide
            </p>
            <div className="flex gap-2">
              <Link to="/admin/add-patient" className="flex-1">
                <Button
                  data-testid="quick-add-patient"
                  className="w-full gap-2"
                  size="sm"
                >
                  <UserPlus className="w-4 h-4" />
                  Adaugă
                </Button>
              </Link>
              <Link to="/admin/patients" className="flex-1">
                <Button
                  data-testid="quick-view-all"
                  variant="outline"
                  className="w-full gap-2"
                  size="sm"
                >
                  Vezi Toți
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vize Completate (calendar) */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <CalendarDays className="w-5 h-5 text-emerald-600" />
            Vize Completate
          </CardTitle>
        </CardHeader>
        <CardContent>
          {vizeByDate.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="vize-empty">
              Nu există vize generate încă. Numărul de ordine se generează după ce toate atelierele unui sportiv sunt completate.
            </p>
          ) : (
            <div className="space-y-2" data-testid="vize-by-date-list">
              {vizeByDate.map((item, index) => (
                <div
                  key={item.date}
                  className={`flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-100 animate-fade-in stagger-${Math.min(index + 1, 5)}`}
                >
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium text-foreground">{formatDateRo(item.date)}</span>
                  </div>
                  <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-800 font-medium">
                    {item.count} {item.count === 1 ? "număr de ordine generat" : "numere de ordine generate"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* === AUDIT & Operational === */}
      <Card className="mb-8" data-testid="audit-card">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <TrendingUp className="w-5 h-5 text-primary" />
            Audit & Operațional
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Vize luna curentă */}
            <div className="p-4 rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20" data-testid="audit-vize-month">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Vize emise — {lunaRo || "luna curentă"}</p>
                  <p className="text-4xl font-bold text-primary mt-1">{vizeThisMonth}</p>
                </div>
                <CalendarDays className="w-8 h-8 text-primary/40" />
              </div>
            </div>

            {/* Pending alerte */}
            <div
              className={`p-4 rounded-lg border ${pendingVize.length > 0 ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-200'}`}
              data-testid="audit-pending-alerts"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {pendingVize.length > 0 ? "Atenție — vize de generat" : "Toate vizele la zi"}
                  </p>
                  <p className={`text-4xl font-bold mt-1 ${pendingVize.length > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    {pendingVize.length}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {pendingVize.length > 0
                      ? "sportivi cu toate atelierele completate dar fără viză emisă"
                      : "niciun sportiv nu așteaptă generarea vizei"}
                  </p>
                </div>
                <AlertCircle className={`w-8 h-8 flex-shrink-0 ${pendingVize.length > 0 ? 'text-amber-500' : 'text-emerald-500/40'}`} />
              </div>
            </div>
          </div>

          {/* Pending list — colapsabilă vizual */}
          {pendingVize.length > 0 && (
            <div className="space-y-2" data-testid="audit-pending-list">
              <p className="text-sm font-medium text-amber-800">Sportivi care necesită apăsarea „Generează nr de ordine&rdquo;:</p>
              <div className="space-y-1.5">
                {pendingVize.map((p) => {
                  const dp = p.date_personale || {};
                  const isd = p.info_sportive || {};
                  return (
                    <Link
                      key={p.id}
                      to={`/admin/patients/${p.id}`}
                      className="flex items-center justify-between p-2.5 rounded-md bg-white border border-amber-200 hover:border-amber-400 hover:bg-amber-50 transition-colors group"
                      data-testid={`pending-vize-row-${p.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Badge variant="outline" className="font-mono flex-shrink-0">#{p.numar_ordine}</Badge>
                        <span className="font-medium truncate">{dp.nume} {dp.prenume}</span>
                        {isd.club_sportiv && (
                          <span className="text-xs text-muted-foreground hidden sm:inline">— {isd.club_sportiv}</span>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-amber-600 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top 5 cluburi */}
          {topClubs.length > 0 && (
            <div data-testid="audit-top-clubs">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-4 h-4 text-amber-500" />
                <p className="text-sm font-medium">Top cluburi după vize generate</p>
              </div>
              <div className="space-y-1.5">
                {topClubs.map((c, i) => {
                  const max = topClubs[0].count;
                  const widthPct = max > 0 ? Math.max(8, (c.count / max) * 100) : 0;
                  const colors = ["bg-amber-500", "bg-slate-400", "bg-amber-700", "bg-slate-300", "bg-slate-300"];
                  return (
                    <div key={c.club} className="flex items-center gap-3" data-testid={`top-club-row-${i}`}>
                      <span className="w-5 text-sm font-bold text-muted-foreground">{i + 1}.</span>
                      <span className="text-sm font-medium min-w-0 truncate flex-shrink-0 w-32 md:w-40">{c.club}</span>
                      <div className="flex-1 h-6 bg-muted/40 rounded-md overflow-hidden relative">
                        <div
                          className={`h-full ${colors[i] || 'bg-slate-300'} transition-all duration-700`}
                          style={{ width: `${widthPct}%` }}
                        />
                        <span className="absolute inset-0 flex items-center px-2 text-xs font-semibold text-foreground">
                          {c.count} {c.count === 1 ? "viză" : "vize"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sports Distribution */}
      {topSports.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              <Dumbbell className="w-5 h-5 text-muted-foreground" />
              Sporturi Populare
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {topSports.map(([sport, count], index) => (
                <div
                  key={sport}
                  className={`p-4 rounded-lg bg-muted animate-fade-in stagger-${index + 1}`}
                >
                  <p className="font-medium">{sport}</p>
                  <p className="text-2xl font-bold text-primary mt-1">{count}</p>
                  <p className="text-xs text-muted-foreground">sportivi</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sportivi Recenți */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-muted-foreground" />
              Sportivi Recenți
            </div>
          </CardTitle>
          <Link to="/admin/patients">
            <Button variant="ghost" size="sm" className="gap-1">
              Vezi Toți
              <ChevronRight className="w-4 h-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentPatients.length === 0 ? (
            <div className="empty-state py-8">
              <Users className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-lg font-medium">Nu există sportivi încă</p>
              <p className="text-sm">Adaugă primul sportiv pentru a începe</p>
              <Link to="/admin/add-patient" className="mt-4">
                <Button data-testid="empty-add-patient">Adaugă Sportiv</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentPatients.map((patient, index) => {
                const dp = patient.date_personale || {};
                const is = patient.info_sportive || {};
                const fullName = `${dp.nume || ''} ${dp.prenume || ''}`.trim() || 'Fără nume';
                const numarOrdine = patient.numar_ordine || 0;
                
                return (
                  <Link
                    key={patient.id}
                    to={`/admin/patients/${patient.id}`}
                    className={`flex items-center gap-4 p-3 rounded-lg table-row-hover animate-fade-in stagger-${index + 1}`}
                    data-testid={`recent-patient-${patient.id}`}
                  >
                    <Badge variant="outline" className="font-mono flex-shrink-0">
                      #{numarOrdine}
                    </Badge>
                    <div className="avatar-placeholder flex-shrink-0">
                      {getInitials(fullName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{fullName}</p>
                      <p className="text-sm text-muted-foreground">
                        {is.sport_practicat || 'Sport nespecificat'} • {dp.sex || 'N/A'}
                      </p>
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      <p>{formatDate(patient.created_at)}</p>
                      {patient.reviews?.length > 0 && (
                        <p className="text-xs">
                          {patient.reviews.length} consultați{patient.reviews.length !== 1 ? "i" : "e"}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
